import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { Heading } from "../../components";
import TopMenuBar from "components/TopMenuBar";
import FooterSection from "components/FooterSection";
import { AGENT_TEMPLATES } from "./agentTemplates";
import "./style.css";

const TONE_OPTIONS = [
    { value: "friendly", label: "Friendly" },
    { value: "professional", label: "Professional" },
    { value: "casual", label: "Casual" },
];

const TONE_LINES = {
    friendly: "Speak in a warm, friendly, approachable tone.",
    professional: "Speak in a polished, professional tone.",
    casual: "Speak in a relaxed, casual, conversational tone.",
};

function buildPersonaPrompt({ businessName, industry, task, tone }) {
    const toneLine = TONE_LINES[tone] || TONE_LINES.friendly;
    return (
        `You are now a customer support agent for ${businessName}, a ${industry} business. ${toneLine} `
        + `Your job: ${task}. Stay fully in this role for the rest of the conversation — do not mention `
        + `that this is a demo, a test, or that you are an AI system unless directly asked. If the caller `
        + `asks something outside what you'd realistically know as ${businessName}'s support agent, `
        + `politely say you'll need to check and follow up.`
    );
}

export default function BuildAgentDemo() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [business, setBusiness] = useState({
        businessName: "",
        industry: "",
        task: "",
        tone: "friendly",
    });
    const [account, setAccount] = useState({ email: "", password: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);

    function handleBusinessChange(e) {
        setBusiness((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        setSelectedTemplateId(null);
    }

    function handleUseTemplate(template) {
        setBusiness({
            businessName: template.sampleBusinessName,
            industry: template.industry,
            task: template.task,
            tone: template.tone,
        });
        setSelectedTemplateId(template.id);
    }

    function handleAccountChange(e) {
        setAccount((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }

    function handleContinue(e) {
        e.preventDefault();
        setStep(2);
    }

    async function handleSignup(e) {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const response = await fetch("https://aihapi.onrender.com/api/v1/auth/signup", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: account.email, password: account.password }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(payload.detail || "Signup failed. Please try again.");
            }
            navigate("/demo/voice-agent", {
                state: {
                    personaPrompt: buildPersonaPrompt(business),
                    businessName: business.businessName,
                },
            });
        } catch (err) {
            setError(err.message || "Something went wrong. Please try again.");
        }
        setLoading(false);
    }

    return (
        <>
            <Helmet>
                <title>Build Your Own Support Agent — AIHumanity</title>
                <meta
                    name="description"
                    content="Describe your business and talk to a live, real-time voice agent built to role-play customer support for it — powered by AIHumanity's relationship layer."
                />
            </Helmet>

            <div className="build-agent-page">
                <TopMenuBar />

                <section className="build-agent-hero">
                    <span className="build-agent-badge">Build Your Own Demo</span>
                    <Heading size="3xl" as="h1" className="!text-transparent bg-gradient5 bg-clip-text">
                        Hear what your business's AI agent could sound like
                    </Heading>
                    <p>
                        Describe a business — real or a quick example, like a travel agency doing
                        pre-consultation calls — and talk to a live voice agent built to role-play
                        customer support for it, in real time.
                    </p>
                </section>

                <section className="build-agent-wizard">
                    <div className="build-agent-steps">
                        <span className={step === 1 ? "active" : "done"}>1. Describe your business</span>
                        <span className={step === 2 ? "active" : ""}>2. Sign up to test</span>
                    </div>

                    {step === 1 && (
                        <>
                            <div className="build-agent-templates">
                                <span className="build-agent-templates-label">Or start from a template</span>
                                <div className="build-agent-template-grid">
                                    {AGENT_TEMPLATES.map((template) => (
                                        <button
                                            type="button"
                                            key={template.id}
                                            className={`build-agent-template-card${selectedTemplateId === template.id ? " active" : ""}`}
                                            onClick={() => handleUseTemplate(template)}
                                        >
                                            <span className="build-agent-template-icon">{template.icon}</span>
                                            <strong>{template.label}</strong>
                                            <p>{template.description}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {selectedTemplateId && (
                                <p className="build-agent-template-note">
                                    Prefilled from the <strong>
                                        {AGENT_TEMPLATES.find((t) => t.id === selectedTemplateId)?.label}
                                    </strong> template — edit any field below, especially the business name, to make it yours.
                                </p>
                            )}

                            <form className="build-agent-form" onSubmit={handleContinue}>
                            <label>
                                Business name
                                <input
                                    name="businessName"
                                    placeholder="e.g. Wanderlust Travel Co."
                                    value={business.businessName}
                                    onChange={handleBusinessChange}
                                    required
                                />
                            </label>
                            <label>
                                Industry / business type
                                <input
                                    name="industry"
                                    placeholder="e.g. Travel agency"
                                    value={business.industry}
                                    onChange={handleBusinessChange}
                                    required
                                />
                            </label>
                            <label>
                                What should the agent help with?
                                <textarea
                                    name="task"
                                    placeholder="e.g. Answer questions about vacation packages and gather trip preferences during a pre-consultation call"
                                    value={business.task}
                                    onChange={handleBusinessChange}
                                    required
                                />
                            </label>
                            <label>
                                Tone
                                <select name="tone" value={business.tone} onChange={handleBusinessChange}>
                                    {TONE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </label>
                            <button type="submit" className="build-agent-primary">Continue</button>
                            </form>
                        </>
                    )}

                    {step === 2 && (
                        <form className="build-agent-form" onSubmit={handleSignup}>
                            <p className="build-agent-step-note">
                                One quick account so we can gate access to live calls. This is only used
                                to let you test — no spam.
                            </p>
                            <label>
                                Email address
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="you@example.com"
                                    value={account.email}
                                    onChange={handleAccountChange}
                                    required
                                />
                            </label>
                            <label>
                                Password
                                <input
                                    type="password"
                                    name="password"
                                    placeholder="At least 6 characters"
                                    minLength={6}
                                    value={account.password}
                                    onChange={handleAccountChange}
                                    required
                                />
                            </label>
                            {error && <p className="build-agent-error">{error}</p>}
                            <div className="build-agent-form-actions">
                                <button type="button" className="build-agent-secondary" onClick={() => setStep(1)}>
                                    Back
                                </button>
                                <button type="submit" className="build-agent-primary" disabled={loading}>
                                    {loading ? "Creating account…" : "Create account & start test call"}
                                </button>
                            </div>
                        </form>
                    )}
                </section>

                <FooterSection />
            </div>
        </>
    );
}
